import { SocialUser } from '@abacritt/angularx-social-login';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IMembershipDetails } from '@app/tickets/tickets.model';
import { Observable, share } from 'rxjs';
import { InitialConfigService } from '../app-config/initial-config.service';
import { IGuestAuthJwt } from '../auth/auth.model';
import { blankUserAPI, IUserAPI } from '../booking/models/user-api.model';
import { IBasicCustomer } from '../customer/basic-customer.model';
import { CStorageKeys } from '../storage/storage-keys.const';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private readonly storageKeys = CStorageKeys;

  private userAccount = blankUserAPI();

  constructor(
    private http: HttpClient,
    private initialConfigService: InitialConfigService,
    private storageService: StorageService
  ) {}

  getUserAccount(): IUserAPI {
    return this.userAccount;
  }

  setUserAccount(user: IUserAPI): void {
    this.userAccount = user;
    this.setStorageUserAccount(user);
  }

  clearUserAccount(): void {
    this.userAccount = blankUserAPI();
    this.storageService.removeItem(this.storageKeys.accountDetails, true);
  }

  /**
   * Sets the guest account in local storage
   * @param user Guest account object
   */
  setStorageUserAccount(user: IUserAPI): void {
    this.storageService.setItem(this.storageKeys.accountDetails, JSON.stringify(user), true);
  }

  /**
   * Gets the user account stored in local storage
   * @returns user account as a interface or undefined
   */
  getStorageUserAccount(): IUserAPI | undefined {
    const account = this.storageService.getItem<IUserAPI>(this.storageKeys.accountDetails, true);
    if (!!account && typeof account !== 'string') {
      return account;
    }

    return undefined;
  }

  /**
   * Merges a social user details with local storage user information. Prefers SocialUser data
   * @param user SocialUser instance
   * @returns merged storage UserAPI with updated guest details
   */
  mapSocialUserToStorageUserAPI(user: SocialUser): IUserAPI {
    const storage = this.getUserAccount();

    return {
      ...storage,
      email: user.email ?? storage?.email,
      firstName: user.firstName ?? storage?.firstName,
      lastName: user.lastName ?? storage?.lastName,
      phone: storage?.phone ?? '',
      callingCode: storage?.callingCode
    };
  }

  /**
   * Merges fetched guest details with local storage user information
   * @param guest account details
   * @returns merged storage  UserAPI with updated guest details
   */
  mapBasicCustomerToStorageUserAPI(guest: IBasicCustomer | Partial<IBasicCustomer>): IUserAPI {
    const storage = this.getUserAccount();

    return {
      ...storage,
      email: guest.email ?? storage?.email ?? '',
      phone: guest.mobile || storage?.phone || '',
      firstName: guest.firstName ?? storage?.firstName ?? '',
      lastName: guest.lastName ?? storage?.lastName ?? '',
      acceptMarketing: guest.acceptMarketing ?? storage?.acceptMarketing,
      postCode: guest.postcode ?? storage?.postCode,
      customerId: guest.customerId ?? storage?.customerId,
      callingCode: guest.mobileCountryCode ? parseInt(guest.mobileCountryCode) : storage?.callingCode,
      taxIdentificationNumber: guest.taxIdentificationNumber ?? storage?.taxIdentificationNumber
    };
  }

  /**
   * Merges basic customer information from account fetch with local storage values and memory values
   * This should happened whenever fetching the customer account from the API
   * @param guest basic customer information
   */
  mergeAndSetAccounts(guest: IBasicCustomer | Partial<IBasicCustomer>): void {
    const mergedDetails = this.mapBasicCustomerToStorageUserAPI(guest);
    this.setUserAccount(mergedDetails);
  }

  /**
   * Maps the UserApi interface (used in guest details form) to partial basic customer
   * Primarily ensures that phone is mapped to the mobile property for the API
   * @param userApi
   */
  mapUserApiToBasicCustomer(userApi: IUserAPI): Partial<IBasicCustomer> {
    return {
      firstName: userApi.firstName,
      lastName: userApi.lastName,
      mobileCountryCode: userApi.callingCode?.toString(),
      mobile: userApi.phone ? '+' + userApi.callingCode + userApi.phone : undefined,
      email: userApi.email,
      acceptMarketing: userApi.acceptMarketing,
      postcode: userApi.postCode,
      taxIdentificationNumber: userApi.taxIdentificationNumber
    };
  }

  /**
   * Calls the API to request an account creation email to be sent to the specified guest/customer
   * @param customerUniqueId from draft booking
   * @param autoCreated to indicate that account is created during checkout
   * @returns http request observable
   */
  requestAccountCreation(customerUniqueId: string, autoCreated = false): Observable<undefined> {
    const config = this.initialConfigService.getInitialConfig();
    return this.http.post<undefined>(`${config.baseApiUrl}/checkout/user/create`, {
      customerUniqueId,
      checkoutSlug: config.checkoutSlug,
      autoCreated
    });
  }

  /**
   * Calls the API to verify an account via verification code and receive an auth JWT
   * @param verificationCode query param from navigating via email verification link
   * @returns http request observable - 200 success with auth JWT or 409 error
   */
  verifyEmailByCode(code: string): Observable<IGuestAuthJwt> {
    return this.http.post<IGuestAuthJwt>(`${this.initialConfigService.getBaseApiUrl()}/checkout/user/verify-email`, {
      code
    });
  }

  /**
   * Calls the API to fetch a guest's account
   * @returns http request observable - 200 User API DTO or 409 error
   */
  fetchGuestAccount(): Observable<IBasicCustomer> {
    return this.http
      .get<IBasicCustomer>(`${this.initialConfigService.getBaseApiUrl()}/checkout/user/details`)
      .pipe(share());
  }

  /**
   * Calls the API to submit customer details during sign up and receive an auth token
   * @param payload create customer specific DTO, with unique sign up GUID
   * @returns http request observable - 200 auth token or 409 error
   */
  createCustomer(payload: Partial<IBasicCustomer>): Observable<void> {
    return this.http.post<void>(
      `${this.initialConfigService.getBaseApiUrl()}/checkout/user/create-customer/v2`,
      payload
    );
  }

  /**
   * Send request to the server for updating account details.
   * @param accountDetails IBasicCustomer object with account details.
   * @returns string message of success or failure.
   */
  updateAccountDetails(accountDetails: Partial<IBasicCustomer>): Observable<string> {
    return this.http.post<string>(`${this.initialConfigService.getBaseApiUrl()}/checkout/user/details`, accountDetails);
  }

  /**
   * Sends an array of product Ids to request the guest's applicable memberships for the products
   * @param productIds unique list of product Ids (likely checkout basket items)
   * @returns list of unique memberships that directly affect one (or multiple) product
   */
  getProductApplicableMemberships(productIds: number[]): Observable<IMembershipDetails[]> {
    return this.http.post<IMembershipDetails[]>(
      `${this.initialConfigService.getBaseApiUrl()}/checkout/user/membership-discounts`,
      {
        productIds
      }
    );
  }
}
